/* Common imports */

import { ObjectId } from "bson";

/* Component Imports */

import PropertyCard from "../../../../cards/PropertyCardDashboardEditorView/PropertyCardFetcher";

/* Redux imports */

import StateInterface from "../../../../../redux-magic/state-interface";
import { BeegruUserProfileContext } from "../../../../../redux-magic/sub-interfaces/redux-session-interface";

/* mui imports */

import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	styled,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";

/* Icom imports */

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

/* NexrRouter import */

import { Close, InfoOutlined } from "@mui/icons-material";
import { NextRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
import BroadcastBannerPropertyUploadSuccess from "../../../../page-sections/Broadcasts/BroadcastBannerPropertyUploadSuccess";

/* Library imports */

/* Stylings */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
		},
		minHeight: "700px",
		maxHeight: "100%",
		borderRadius: "1rem",
		position: "relative",
	},
	"& .MuiDialogTitle-root + .MuiDialogContent-root": {
		paddingTop: "1rem",
		borderTop: "none",
	},
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-end",
	width: "100%",
	fontSize: "1.25rem",
	fontWeight: 500,
	lineHeight: "106%",
	background: "#12744F",
	gap: "0rem",
	// padding: "1rem ",
	borderRadius: "1rem 1rem 0rem 0rem",
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
	padding: "1rem",
	background: "#12744F",
	overflow: "auto",
}));

const CustomDialogActions = styled(DialogActions)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
	padding: "0rem",
	background: "#12744F",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "space-between",
}));

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "space-between",
}));

const InfoContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	background: "#fdf0e5",
	borderRadius: "1rem 1rem 0rem 0rem",
	gap: "0.5rem",
	width: "100%",
	padding: "0.5rem 1rem",
}));

const InfoText = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	fontWeight: 400,
	color: "#5f2b01",
}));

const SuccessHeadText = styled(Typography)(({ theme }) => ({
	fontSize: "1.5rem",
	fontWeight: 700,
	lineHeight: "133.4%",
	color: "#FFF",
	textAlign: "center",
}));

const SuccessPage = ({
	session,
	router,
	dispatch,
	open,
	propertyId,
	profile_context,
	handleClose,
	businessType,
	new_listing_url,
}: {
	session: StateInterface["redux_session"];
	router: NextRouter;
	dispatch: Function;
	open: boolean;
	propertyId: ObjectId | string;
	businessType: string;
	new_listing_url: string;
	profile_context: BeegruUserProfileContext;
	handleClose: () => void;
}) => {
	const theme = useTheme();

	const smDevice: boolean = useMediaQuery(theme.breakpoints.down("sm"));

	const localLang = {
		your_property_has_been_created_successfully:
			router.locale === "ar-AE"
				? "!تم إنشاء عقارك بنجاح"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಆಸ್ತಿ ಯಶಸ್ವಿಯಾಗಿ ರಚಿಸಲಾಗಿದೆ!"
					: "Your property has been created successfully!",
		infoText:
			router.locale === "ar-AE"
				? "استخدام البث يمكن أن يزيد فرص الحصول على العملاء المحتملين بمقدار 10 مرات"
				: router.locale === "kn-IN"
					? "ಪ್ರಸಾರವನ್ನು ಬಳಸುವುದು ಲೀಡ್‌ಗಳನ್ನು ಪಡೆಯುವ ಅವಕಾಶವನ್ನು 10 ಪಟ್ಟು ಹೆಚ್ಚಿಸಬಹುದು"
					: "Using broadcast can increase your chances of getting leads by 10x",
		inReview: router.locale === "ar-AE" ? "في المراجعة" : router.locale === "kn-IN" ? "ಪರಿಶೀಲನೆಯಲ್ಲಿ" : "In Review",
		go_to_my_listings:
			router.locale === "ar-AE"
				? "اذهب إلى إعلاناتي"
				: router.locale === "kn-IN"
					? "ನನ್ನ ಪಟ್ಟಿ ಗೆ ಹೋಗಿ"
					: "Go to my listings",
		done: router.locale === "ar-AE" ? "تم" : router.locale === "kn-IN" ? "ಮುಗಿಯಿತು" : "Done",

		your_listing_will_be_live_after_moderation:
			router.locale === "ar-AE"
				? "سيكون إعلانك متاحًا بعد المراجعة"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಪಟ್ಟಿ ಪರಿಶೀಲನೆಯ ನಂತರ ಲೈವ್ ಆಗಿರುತ್ತದೆ"
					: "Your listing will be live after moderation",
	};

	return (
		<div style={{ width: "100%", height: "100%", background: "#12744F" }}>
			<RowContainer
				sx={{
					width: "100%",
					padding: "0.5rem 0.5rem 0rem 0rem",
					justifyContent: "flex-end",
					alignItems: "flex-end",
				}}
			>
				<IconButton
					aria-label="close"
					onClick={() => {
						handleClose();
					}}
				>
					<Close
						fontSize="medium"
						sx={{
							color: theme.palette.text.disabled,
						}}
					/>
				</IconButton>
			</RowContainer>

			{/* <CustomDialogContent> */}
			{/* Success message container */}

			<ColumnContainer
				sx={{
					padding: "1rem",
					justifyContent: "center",
					alignItems: "center",
					gap: "1rem",
				}}
			>
				{/*  Success message */}

				<CheckCircleOutlineIcon
					sx={{
						height: "4.5rem",
						width: "4.5rem",
						color: "#FFF",
					}}
				/>

				<SuccessHeadText>{localLang.your_property_has_been_created_successfully}</SuccessHeadText>

				{/* Property card container */}

				<ColumnContainer
					sx={{
						width: "100%",
					}}
				>
					<InfoContainer>
						<InfoOutlined
							sx={{
								color: "#ed6c02",
							}}
						/>
						<InfoText variant="body2">{localLang.your_listing_will_be_live_after_moderation}</InfoText>
					</InfoContainer>
					<RowContainer
						sx={{
							padding: "1rem 0.5rem",
							width: "100%",
							borderRadius: "0rem 0rem 1rem 1rem",
							background: theme.palette.background.paper,
						}}
					>
						<PropertyCard
							PropertyId={propertyId}
							dispatch={dispatch}
							session={session}
							profile_context={profile_context}
							show_only_listing_details={true}
						/>
					</RowContainer>
				</ColumnContainer>
			</ColumnContainer>
			{/* </CustomDialogContent> */}

			{/* Bottom Container */}

			{/* <CustomDialogActions> */}
			<ColumnContainer
				sx={{
					width: "100%",
					gap: "0.5rem",
					borderRadius: "1rem 1rem 0rem 0rem",
					padding: "1rem 1rem 1.5rem 1rem",
					background: theme.palette.background.paper,
				}}
			>
				{/* Broadcast Banner */}

				<BroadcastBannerPropertyUploadSuccess
					session={session}
					dispatch={dispatch}
					router={router}
				/>

				{/* Go to listings Button */}

				<BeegruButton
					flavor="primary"
					variant="outlined"
					size="medium"
					fullWidth
					onClick={() => {
						window.location.replace(`/${businessType}s/${new_listing_url}?screen=listings`);
					}}
					sx={{
						"&.MuiButton-outlined": {
							color: "#621b16",
							border: `1px solid #621b16`,
							"&:hover": {
								background: "transparent",
							},
							"&:active": {
								background: "transparent",
							},
							"&:focus": {
								background: "transparent",
							},
						},
					}}
				>
					{localLang.go_to_my_listings}
				</BeegruButton>
			</ColumnContainer>
		</div>
	);
};

export default SuccessPage;
